import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import Button from '../../../components/button/Button';
import { PERIOD, SECTION_IDS } from '../../../constants/shared/CommonConstants';
import { getLegendPanelTitle } from '../../legend/shared/legendUtils';
import { getGradeTitle } from '../../../constants/shared/GradesConstants';
import { usePrintPage } from '../../../utils/shared/printUtils';
import RunningGirlSection from './RunningGirlSection';
import './ProbeDetailsPageHeader.scss';
import { generateReactKey } from '../../../utils/shared/commonUtils';
import { CLOSE_PROBE_DETAILS } from '../../../constants/reading/DemoModeConstants';

const {
    SS_PROGRESS_MEASURE_LIST,
    BENCHMARK_RESULT_TABLE,
    MCLASS_HEADER,
    PROGRESS_GRID,
    SS_BH,
    TEXT_READING_HISTORY,
    FEATURE_REPORT_TABLE,
    WIDGETS_BENCHMARK,
} = SECTION_IDS;

const ProbeDetailsPageHeader = props => {
    const {
        student,
        result,
        staff,
        onClose,
        runningGirlsParams,
        howToUnderstandText,
        overallField,
        assessmentDate,
        assessmentName,
        helpUrl,
        showInvalidateModal,
        isInvalidationFeatureAvailable,
        summerPeriodLabel,
    } = props;

    const { period, formGrade: grade, formNumber, measureScores } = result;
    const date = assessmentDate ? `- ${assessmentDate}` : '';
    const { overallLabel, overallValue } = overallField;

    const [onPrint] = usePrintPage([
        SS_PROGRESS_MEASURE_LIST,
        BENCHMARK_RESULT_TABLE,
        FEATURE_REPORT_TABLE,
        TEXT_READING_HISTORY,
        WIDGETS_BENCHMARK,
        MCLASS_HEADER,
        PROGRESS_GRID,
        SS_BH,
    ]);

    return (
        <>
            <div className="sticky-header-container">
                <div className="container">
                    <div className="row name-row">
                        <div data-test-id="header-details" data-tour-allow-interaction>
                            <h2>
                                {student.lastName}, {student.firstName}
                            </h2>
                            <p>
                                {getGradeTitle(grade)}{' '}
                                {period === PERIOD.SUMMER ? summerPeriodLabel : period}
                                {formNumber && ` Form ${formNumber}`}
                            </p>
                            <p>{`Assessment Date ${date}`}</p>
                        </div>
                        <div className="info-col">
                            <Button
                                dataTourAllowTnteraction
                                dataTourStep={CLOSE_PROBE_DETAILS}
                                className="close-btn no-print"
                                text={
                                    <>
                                        <i className="icon-v2-close" />
                                        Close
                                    </>
                                }
                                onClick={onClose}
                            />
                            <div className="d-flex align-items-center no-print">
                                <Button
                                    className="print-btn"
                                    text={
                                        <>
                                            <i className="icon-v2-print" />
                                            Print
                                        </>
                                    }
                                    onClick={onPrint}
                                />
                                <a
                                    href={helpUrl}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    data-test-id="help-btn"
                                    className="btn btn-secondary"
                                >
                                    {getLegendPanelTitle(howToUnderstandText)}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="pd-header-container">
                <div className="container">
                    <div className="row assessment">
                        <div className="row-item">
                            {`Assessment: `}
                            <strong>{assessmentName}</strong>
                        </div>
                        {!isEmpty(overallValue) && (
                            <div className="row-item">
                                {`${overallLabel}: `}
                                <strong className="overall-score">{overallValue}</strong>
                            </div>
                        )}
                        {!isEmpty(staff) && (
                            <div className="row-item">
                                {`Assessor: `}
                                <strong>
                                    {staff.lastName}, {staff.firstName}
                                </strong>
                            </div>
                        )}
                    </div>
                    <div
                        className={`running-girl-container ${
                            !measureScores ? 'no-running-girl' : ''
                        }`}
                    >
                        {measureScores && (
                            <div className="row stats">
                                {runningGirlsParams.map(runningGirlParams => (
                                    <RunningGirlSection
                                        key={generateReactKey(runningGirlParams.measureKey)}
                                        runningGirlParams={runningGirlParams}
                                    />
                                ))}
                            </div>
                        )}

                        {isInvalidationFeatureAvailable && (
                            <Button
                                className="invalidate-button"
                                onClick={showInvalidateModal}
                                text="Invalidate"
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

ProbeDetailsPageHeader.defaultProps = {
    runningGirlsParams: [],
    howToUnderstandText: null,
    assessmentDate: null,
    assessmentName: null,
    overallField: {},
    helpUrl: null,
    showInvalidateModal: () => {},
    isInvalidationFeatureAvailable: false,
};

ProbeDetailsPageHeader.propTypes = {
    staff: PropTypes.object.isRequired,
    student: PropTypes.object.isRequired,
    result: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    assessmentDate: PropTypes.string,
    overallField: PropTypes.object,
    runningGirlsParams: PropTypes.array,
    assessmentName: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    howToUnderstandText: PropTypes.string,
    helpUrl: PropTypes.string,
    showInvalidateModal: PropTypes.func,
    isInvalidationFeatureAvailable: PropTypes.bool,
    summerPeriodLabel: PropTypes.string.isRequired,
};

export default ProbeDetailsPageHeader;
